import * as Immutable from 'immutable';
import { put, all } from 'redux-saga/effects';

import { Page } from 'types/page';
import { Figure } from 'types/figure';
import { Wall } from 'types/wall';
import { WallType } from 'types/wallType';
import { Point } from 'types/point';
import { PointType } from 'types/pointType';
import { Label } from 'types/label';
import { LabelType } from 'types/labelType';
import { SketchSymbol } from 'types/sketchSymbol';
import { PositionedLabel } from 'types/positionedLabel';
import { PositionedSymbol } from 'types/positionedSymbol';
import { AreaType } from 'types/areaType';
import { FigureType } from 'types/figureType';
import { GlaType } from 'types/glaType';
import { SketchSymbolGroup } from 'types/sketchSymbolGroup';
import { actions as pagesActions } from 'ducks/model/pages';
import { actions as areaTypesActions } from 'ducks/model/areaTypes';
import { actions as figuresActions } from 'ducks/model/figures';
import { actions as wallsActions } from 'ducks/model/walls';
import { actions as pointsActions } from 'ducks/model/points';
import { actions as labelsActions } from 'ducks/model/labels';
import { actions as positionedLabelActions } from 'ducks/model/positionedLabels';
import { actions as symbolActions } from 'ducks/model/symbols';
import { actions as symbolGroupActions } from 'ducks/model/symbolGroups';
import { actions as positionedSymbolActions } from 'ducks/model/positionedSymbols';

const pages: Page[] = [
  {
    pageId: 'page1',
    objects: Immutable.Map({
      room1: { objectId: 'room1', computed: false },
      sharedRoom: { objectId: 'sharedRoom', computed: false },
    }),
  },
  {
    pageId: 'page2',
    objects: Immutable.Map({
      room2: { objectId: 'room2', computed: false },
    }),
  },
  {
    pageId: 'shapesPage',
    objects: Immutable.Map({
      clockwiseSquare: { objectId: 'clockwiseSquare', computed: false },
      antiClockwiseSquare: { objectId: 'antiClockwiseSquare', computed: false },
      clockwiseRightRhombus: { objectId: 'clockwiseRightRhombus', computed: false },
      antiClockwiseRightRhombus: { objectId: 'antiClockwiseRightRhombus', computed: false },
      clockwiseLeftRhombus: { objectId: 'clockwiseLeftRhombus', computed: false },
      antiClockwiseLeftRhombus: { objectId: 'antiClockwiseLeftRhombus', computed: false },
      clockwiseUpRhombus: { objectId: 'clockwiseUpRhombus', computed: false },
      antiClockwiseUpRhombus: { objectId: 'antiClockwiseUpRhombus', computed: false },
      clockwiseDownRhombus: { objectId: 'clockwiseDownRhombus', computed: false },
      antiClockwiseDownRhombus: { objectId: 'antiClockwiseDownRhombus', computed: false },
      concaveRoom: { objectId: 'concaveRoom', computed: false },
    }),
  },
];

const figures: Figure[] = [
  {
    figureId: 'room1',
    walls: ['w01', 'w02', 'w03', 'w22', 'w21'],
    type: FigureType.BASE,
  },
  {
    figureId: 'room2',
    walls: ['w5', 'w6', 'w7', 'w8'],
    type: FigureType.BASE,
  },
  {
    figureId: 'nonClosedPath',
    walls: [], // ['w03', 'w22', 'w21'],
    type: FigureType.BASE,
  },
  {
    figureId: 'sharedRoom',
    walls: ['w01', 'w02', 'w03', 'w22', 'w21'],
    type: FigureType.BASE,
  },
  {
    figureId: 'clockwiseSquare',
    walls: ['w31-32', 'w32-33', 'w33-34', 'w34-31'],
    type: FigureType.BASE,
  },
  {
    figureId: 'antiClockwiseSquare',
    walls: ['w41-42', 'w42-43', 'w43-44', 'w44-41'],
    type: FigureType.BASE,
  },
  {
    figureId: 'clockwiseRightRhombus',
    walls: ['w51-52', 'w52-53', 'w53-54', 'w54-51'],
    type: FigureType.BASE,
  },
  {
    figureId: 'antiClockwiseRightRhombus',
    walls: ['w61-62', 'w62-63', 'w63-64', 'w64-61'],
    type: FigureType.BASE,
  },
  {
    figureId: 'clockwiseLeftRhombus',
    walls: ['w71-72', 'w72-73', 'w73-74', 'w74-71'],
    type: FigureType.BASE,
  },
  {
    figureId: 'antiClockwiseLeftRhombus',
    walls: ['w81-82', 'w82-83', 'w83-84', 'w84-81'],
    type: FigureType.BASE,
  },
  {
    figureId: 'clockwiseUpRhombus',
    walls: ['w91-92', 'w92-93', 'w93-94', 'w94-91'],
    type: FigureType.BASE,
  },
  {
    figureId: 'antiClockwiseUpRhombus',
    walls: ['w101-102', 'w102-103', 'w103-104', 'w104-101'],
    type: FigureType.BASE,
  },
  {
    figureId: 'clockwiseDownRhombus',
    walls: ['w111-112', 'w112-113', 'w113-114', 'w114-111'],
    type: FigureType.BASE,
  },
  {
    figureId: 'antiClockwiseDownRhombus',
    walls: ['w121-122', 'w122-123', 'w123-124', 'w124-121'],
    type: FigureType.BASE,
  },
  {
    figureId: 'concaveRoom',
    walls: [
      'w131-132', 'w132-133', 'w133-134', 'w134-135',
      'w135-136', 'w136-137', 'w137-138', 'w138-131',
    ],
    type: FigureType.BASE,
  },
];

const walls: Wall[] = [
  {
    wallId: 'w01',
    wallType: WallType.ARC,
    points: ['p2', 'p21', 'arcPoint'],
  },
  {
    wallId: 'w02',
    wallType: WallType.LINE,
    points: ['p21', 'p22'],
  },
  {
    wallId: 'w03',
    wallType: WallType.LINE,
    points: ['p22', 'p3'],
  },
  {
    wallId: 'w0',
    wallType: WallType.LINE,
    points: ['p0', 'p1'],
  },
  {
    wallId: 'w1',
    wallType: WallType.LINE,
    points: ['p1', 'p2'],
  },
  {
    wallId: 'w21',
    wallType: WallType.LINE,
    points: ['p2', 'p2mid'],
  },
  {
    wallId: 'w22',
    wallType: WallType.LINE,
    points: ['p2mid', 'p3'],
  },
  {
    wallId: 'w3',
    wallType: WallType.LINE,
    points: ['p3', 'p4'],
  },
  {
    wallId: 'w4',
    wallType: WallType.LINE,
    points: ['p4', 'p0'],
  },
  {
    wallId: 'w5',
    wallType: WallType.LINE,
    points: ['p5', 'p6'],
  },
  {
    wallId: 'w6',
    wallType: WallType.LINE,
    points: ['p6', 'p7'],
  },
  {
    wallId: 'w7',
    wallType: WallType.LINE,
    points: ['p7', 'p8'],
  },
  {
    wallId: 'w8',
    wallType: WallType.LINE,
    points: ['p8', 'p5'],
  },
  // clockwise square
  {
    wallId: 'w31-32',
    wallType: WallType.LINE,
    points: ['p31', 'p32'],
  },
  {
    wallId: 'w32-33',
    wallType: WallType.LINE,
    points: ['p32', 'p33'],
  },
  {
    wallId: 'w33-34',
    wallType: WallType.LINE,
    points: ['p33', 'p34'],
  },
  {
    wallId: 'w34-31',
    wallType: WallType.LINE,
    points: ['p34', 'p31'],
  },
  // anti-clockwise square
  {
    wallId: 'w41-42',
    wallType: WallType.LINE,
    points: ['p41', 'p42'],
  },
  {
    wallId: 'w42-43',
    wallType: WallType.LINE,
    points: ['p42', 'p43'],
  },
  {
    wallId: 'w43-44',
    wallType: WallType.LINE,
    points: ['p43', 'p44'],
  },
  {
    wallId: 'w44-41',
    wallType: WallType.LINE,
    points: ['p44', 'p41'],
  },
  // clockwise right leaning rhombus
  {
    wallId: 'w51-52',
    wallType: WallType.LINE,
    points: ['p51', 'p52'],
  },
  {
    wallId: 'w52-53',
    wallType: WallType.LINE,
    points: ['p52', 'p53'],
  },
  {
    wallId: 'w53-54',
    wallType: WallType.LINE,
    points: ['p53', 'p54'],
  },
  {
    wallId: 'w54-51',
    wallType: WallType.LINE,
    points: ['p54', 'p51'],
  },
  // anti-clockwise right leaning rhombus
  {
    wallId: 'w61-62',
    wallType: WallType.LINE,
    points: ['p61', 'p62'],
  },
  {
    wallId: 'w62-63',
    wallType: WallType.LINE,
    points: ['p62', 'p63'],
  },
  {
    wallId: 'w63-64',
    wallType: WallType.LINE,
    points: ['p63', 'p64'],
  },
  {
    wallId: 'w64-61',
    wallType: WallType.LINE,
    points: ['p64', 'p61'],
  },
  // clockwise left leaning rhombus
  {
    wallId: 'w71-72',
    wallType: WallType.LINE,
    points: ['p71', 'p72'],
  },
  {
    wallId: 'w72-73',
    wallType: WallType.LINE,
    points: ['p72', 'p73'],
  },
  {
    wallId: 'w73-74',
    wallType: WallType.LINE,
    points: ['p73', 'p74'],
  },
  {
    wallId: 'w74-71',
    wallType: WallType.LINE,
    points: ['p74', 'p71'],
  },
  // anti-clockwise left leaning rhombus
  {
    wallId: 'w81-82',
    wallType: WallType.LINE,
    points: ['p81', 'p82'],
  },
  {
    wallId: 'w82-83',
    wallType: WallType.LINE,
    points: ['p82', 'p83'],
  },
  {
    wallId: 'w83-84',
    wallType: WallType.LINE,
    points: ['p83', 'p84'],
  },
  {
    wallId: 'w84-81',
    wallType: WallType.LINE,
    points: ['p84', 'p81'],
  },
  // clockwise upwards rhombus
  {
    wallId: 'w91-92',
    wallType: WallType.LINE,
    points: ['p91', 'p92'],
  },
  {
    wallId: 'w92-93',
    wallType: WallType.LINE,
    points: ['p92', 'p93'],
  },
  {
    wallId: 'w93-94',
    wallType: WallType.LINE,
    points: ['p93', 'p94'],
  },
  {
    wallId: 'w94-91',
    wallType: WallType.LINE,
    points: ['p94', 'p91'],
  },
  // anti-clockwise upwards rhombus
  {
    wallId: 'w101-102',
    wallType: WallType.LINE,
    points: ['p101', 'p102'],
  },
  {
    wallId: 'w102-103',
    wallType: WallType.LINE,
    points: ['p102', 'p103'],
  },
  {
    wallId: 'w103-104',
    wallType: WallType.LINE,
    points: ['p103', 'p104'],
  },
  {
    wallId: 'w104-101',
    wallType: WallType.LINE,
    points: ['p104', 'p101'],
  },
  // clockwise downwards rhombus
  {
    wallId: 'w111-112',
    wallType: WallType.LINE,
    points: ['p111', 'p112'],
  },
  {
    wallId: 'w112-113',
    wallType: WallType.LINE,
    points: ['p112', 'p113'],
  },
  {
    wallId: 'w113-114',
    wallType: WallType.LINE,
    points: ['p113', 'p114'],
  },
  {
    wallId: 'w114-111',
    wallType: WallType.LINE,
    points: ['p114', 'p111'],
  },
  // anti-clockwise downwards rhombus
  {
    wallId: 'w121-122',
    wallType: WallType.LINE,
    points: ['p121', 'p122'],
  },
  {
    wallId: 'w122-123',
    wallType: WallType.LINE,
    points: ['p122', 'p123'],
  },
  {
    wallId: 'w123-124',
    wallType: WallType.LINE,
    points: ['p123', 'p124'],
  },
  {
    wallId: 'w124-121',
    wallType: WallType.LINE,
    points: ['p124', 'p121'],
  },
  // concave room
  {
    wallId: 'w131-132',
    wallType: WallType.LINE,
    points: ['p131', 'p132'],
  },
  {
    wallId: 'w132-133',
    wallType: WallType.LINE,
    points: ['p132', 'p133'],
  },
  {
    wallId: 'w133-134',
    wallType: WallType.LINE,
    points: ['p133', 'p134'],
  },
  {
    wallId: 'w134-135',
    wallType: WallType.LINE,
    points: ['p134', 'p135'],
  },
  {
    wallId: 'w135-136',
    wallType: WallType.LINE,
    points: ['p135', 'p136'],
  },
  {
    wallId: 'w136-137',
    wallType: WallType.LINE,
    points: ['p136', 'p137'],
  },
  {
    wallId: 'w137-138',
    wallType: WallType.LINE,
    points: ['p137', 'p138'],
  },
  {
    wallId: 'w138-131',
    wallType: WallType.LINE,
    points: ['p138', 'p131'],
  },
];

const points: Point[] = [
  {
    pointId: 'arcPoint',
    x: 330,
    y: 90,
    pointType: PointType.ARC,
  },
  {
    pointId: 'p21',
    x: 460,
    y: 150,
  },
  {
    pointId: 'p22',
    x: 460,
    y: 300,
  },
  {
    pointId: 'p0',
    x: 100,
    y: 200,
  },
  {
    pointId: 'p1',
    x: 100,
    y: 150,
  },
  {
    pointId: 'p2',
    x: 200,
    y: 150,
  },
  {
    pointId: 'p2mid',
    x: 200,
    y: 250,
  },
  {
    pointId: 'p3',
    x: 200,
    y: 300,
  },
  {
    pointId: 'p4',
    x: 100,
    y: 300,
  },
  {
    pointId: 'p5',
    x: 705,
    y: 93,
  },
  {
    pointId: 'p6',
    x: 904,
    y: 106,
  },
  {
    pointId: 'p7',
    x: 912,
    y: 300,
  },
  {
    pointId: 'p8',
    x: 606,
    y: 307,
  },
  // clockwise square
  {
    pointId: 'p31',
    x: 100,
    y: 600,
  },
  {
    pointId: 'p32',
    x: 100,
    y: 500,
  },
  {
    pointId: 'p33',
    x: 200,
    y: 500,
  },
  {
    pointId: 'p34',
    x: 200,
    y: 600,
  },
  // anti-clockwise square
  {
    pointId: 'p41',
    x: 300,
    y: 600,
  },
  {
    pointId: 'p42',
    x: 400,
    y: 600,
  },
  {
    pointId: 'p43',
    x: 400,
    y: 500,
  },
  {
    pointId: 'p44',
    x: 300,
    y: 500,
  },
  // clockwise right leaning rhombus
  {
    pointId: 'p51',
    x: 500,
    y: 600,
  },
  {
    pointId: 'p52',
    x: 540,
    y: 500,
  },
  {
    pointId: 'p53',
    x: 640,
    y: 500,
  },
  {
    pointId: 'p54',
    x: 600,
    y: 600,
  },
  // anti-clockwise right leaning rhombus
  {
    pointId: 'p61',
    x: 700,
    y: 600,
  },
  {
    pointId: 'p62',
    x: 800,
    y: 600,
  },
  {
    pointId: 'p63',
    x: 840,
    y: 500,
  },
  {
    pointId: 'p64',
    x: 740,
    y: 500,
  },
  // clockwise left leaning rhombus
  {
    pointId: 'p71',
    x: 900,
    y: 500,
  },
  {
    pointId: 'p72',
    x: 1000,
    y: 500,
  },
  {
    pointId: 'p73',
    x: 1040,
    y: 600,
  },
  {
    pointId: 'p74',
    x: 940,
    y: 600,
  },
  // anti-clockwise left leaning rhombus
  {
    pointId: 'p81',
    x: 1100,
    y: 500,
  },
  {
    pointId: 'p82',
    x: 1140,
    y: 600,
  },
  {
    pointId: 'p83',
    x: 1240,
    y: 600,
  },
  {
    pointId: 'p84',
    x: 1200,
    y: 500,
  },
  // clockwise upwards rhombus
  {
    pointId: 'p91',
    x: 500,
    y: 800,
  },
  {
    pointId: 'p92',
    x: 500,
    y: 700,
  },
  {
    pointId: 'p93',
    x: 600,
    y: 660,
  },
  {
    pointId: 'p94',
    x: 600,
    y: 760,
  },
  // anti-clockwise upwards rhombus
  {
    pointId: 'p101',
    x: 700,
    y: 800,
  },
  {
    pointId: 'p102',
    x: 800,
    y: 760,
  },
  {
    pointId: 'p103',
    x: 800,
    y: 660,
  },
  {
    pointId: 'p104',
    x: 700,
    y: 700,
  },
  // clockwise downwards rhombus
  {
    pointId: 'p111',
    x: 900,
    y: 660,
  },
  {
    pointId: 'p112',
    x: 1000,
    y: 700,
  },
  {
    pointId: 'p113',
    x: 1000,
    y: 800,
  },
  {
    pointId: 'p114',
    x: 900,
    y: 760,
  },
  // anti-clockwise downwards rhombus
  {
    pointId: 'p121',
    x: 1100,
    y: 660,
  },
  {
    pointId: 'p122',
    x: 1100,
    y: 760,
  },
  {
    pointId: 'p123',
    x: 1200,
    y: 800,
  },
  {
    pointId: 'p124',
    x: 1200,
    y: 700,
  },
  // concave room
  {
    pointId: 'p131',
    x: 1060,
    y: 100,
  },
  {
    pointId: 'p132',
    x: 1260,
    y: 100,
  },
  {
    pointId: 'p133',
    x: 1260,
    y: 200,
  },
  {
    pointId: 'p134',
    x: 1160,
    y: 200,
  },
  {
    pointId: 'p135',
    x: 1160,
    y: 300,
  },
  {
    pointId: 'p136',
    x: 1360,
    y: 300,
  },
  {
    pointId: 'p137',
    x: 1360,
    y: 400,
  },
  {
    pointId: 'p138',
    x: 1060,
    y: 400,
  },
  {
    pointId: 'l1',
    x: 330,
    y: 220,
  },
  {
    pointId: 'l2',
    x: 780,
    y: 200,
  },
  {
    pointId: 'll1',
    x: 300,
    y: 50,
  },
  {
    pointId: 'll2',
    x: 800,
    y: 50,
  },
  {
    pointId: 'ls1',
    x: 700,
    y: 440,
  },
  {
    pointId: 'l3',
    x: 150,
    y: 550,
  },
  {
    pointId: 'l4',
    x: 350,
    y: 550,
  },
  {
    pointId: 'l5',
    x: 575,
    y: 550,
  },
  {
    pointId: 'l6',
    x: 775,
    y: 550,
  },
  {
    pointId: 'l7',
    x: 975,
    y: 550,
  },
  {
    pointId: 'l8',
    x: 1175,
    y: 550,
  },
  {
    pointId: 'l9',
    x: 550,
    y: 725,
  },
  {
    pointId: 'l10',
    x: 750,
    y: 725,
  },
  {
    pointId: 'l11',
    x: 950,
    y: 725,
  },
  {
    pointId: 'l12',
    x: 1150,
    y: 725,
  },
  {
    pointId: 'l13',
    x: 1150,
    y: 150,
  },
];

const areaTypes: AreaType[] = [
  {
    areaTypeId: 'room1AreaType',
    text: 'room 1',
    glaType: GlaType.GLA,
    sortOrder: 1,
  },
  {
    areaTypeId: 'room2AreaType',
    text: 'room 2',
    glaType: GlaType.NON_GLA,
    sortOrder: 2,
  },
  {
    areaTypeId: 'clockwiseSquareAreaType',
    text: 'clockwise square',
    glaType: GlaType.NON_GLA,
    sortOrder: 3,
  },
  {
    areaTypeId: 'antiClockwiseSquareAreaType',
    text: 'anti-clockwise square',
    glaType: GlaType.NON_GLA,
    sortOrder: 4,
  },
  {
    areaTypeId: 'clockwiseRightRhombusAreaType',
    text: 'clockwise right rhombus',
    glaType: GlaType.NON_GLA,
    sortOrder: 5,
  },
  {
    areaTypeId: 'antiClockwiseRightRhombusAreaType',
    text: 'anti-clockwise right rhombus',
    glaType: GlaType.NON_GLA,
    sortOrder: 6,
  },
  {
    areaTypeId: 'clockwiseLeftRhombusAreaType',
    text: 'clockwise right rhombus',
    glaType: GlaType.NON_GLA,
    sortOrder: 7,
  },
  {
    areaTypeId: 'antiClockwiseLeftRhombusAreaType',
    text: 'anti-clockwise right rhombus',
    glaType: GlaType.NON_GLA,
    sortOrder: 8,
  },
  {
    areaTypeId: 'clockwiseUpRhombusAreaType',
    text: 'clockwise up rhombus',
    glaType: GlaType.NON_GLA,
    sortOrder: 9,
  },
  {
    areaTypeId: 'antiClockwiseUpRhombusAreaType',
    text: 'anti-clockwise up rhombus',
    glaType: GlaType.NON_GLA,
    sortOrder: 10,
  },
  {
    areaTypeId: 'clockwiseDownRhombusAreaType',
    text: 'clockwise down rhombus',
    glaType: GlaType.NON_GLA,
    sortOrder: 11,
  },
  {
    areaTypeId: 'antiClockwiseDownRhombusAreaType',
    text: 'anti-clockwise down rhombus',
    glaType: GlaType.NON_GLA,
    sortOrder: 12,
  },
  {
    areaTypeId: 'concaveRoomAreaType',
    text: 'concave room',
    glaType: GlaType.NON_GLA,
    sortOrder: 13,
  },
];

const labels: Label[] = [
  {
    labelId: 'customLabel1',
    type: LabelType.CUSTOM_TEXT,
    text: 'Custom Label 1',
  },
  {
    labelId: 'customLabel2',
    type: LabelType.CUSTOM_TEXT,
    text: 'Custom Label 2',
  },
];

const positionedLabels: PositionedLabel[] = [
  {
    positionedLabelId: 'positionedLabel1',
    labelId: labels[0].labelId,
    pointId: 'p0',
  },
];

const symbolGroups: SketchSymbolGroup[] = [
  {
    symbolGroupId: 'arrows',
    name: 'Arrows',
    description: 'A collection of arrows',
  },
  {
    symbolGroupId: 'staircases',
    name: 'Staircases',
    description: 'A collection of staircases',
  },
  {
    symbolGroupId: 'doors',
    name: 'Doors',
    description: 'A collection of doors',
  },
];

const symbols: SketchSymbol[] = [
  {
    symbolId: 'symbol-DoorLeft90',
    symbolGroupId: 'doors',
    name: 'Door Left 90°',
    width: 53,
    /* eslint-disable max-len */
    data: 'data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMzc2IDM4NCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczpzdmc9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Zz48cGF0aCBmaWxsPSJub25lIiBzdHJva2U9IiMyMzFGMjAiIHN0cm9rZS13aWR0aD0iNyIgc3Ryb2tlLW1pdGVybGltaXQ9IjEwIiBkPSJtMzYzLjMsMzc1LjRjMCwtMjE2LjkgLTE1OC41LC0zNjYuMyAtMzU0LjYsLTM2Ni4zIiBpZD0ic3ZnXzEiLz48bGluZSBmaWxsPSJub25lIiBzdHJva2U9IiMwMDAwMDAiIHN0cm9rZS13aWR0aD0iNDgiIHN0cm9rZS1taXRlcmxpbWl0PSIxMCIgeDE9IjMyIiB5MT0iOS44IiB4Mj0iMzIiIHkyPSIzNzUuOSIgaWQ9InN2Z18yIi8+PC9nPjwvc3ZnPgo=',
  },
  {
    symbolId: 'symbol-ArchitecturalNorth',
    symbolGroupId: 'arrows',
    name: 'Architectural North',
    /* eslint-disable max-len */
    data: 'data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjE2IDIxNiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczpzdmc9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Zz48Y2lyY2xlIGlkPSJzdmdfMSIgcj0iMTAwLjciIGN5PSIxMDcuOSIgY3g9IjEwOCIgc3Ryb2tlLW1pdGVybGltaXQ9IjEwIiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZT0iIzIzMUYyMCIgZmlsbD0ibm9uZSIvPjxwb2x5bGluZSBpZD0ic3ZnXzIiIHBvaW50cz0iMTU5LjEsMTk0IDE1OS4xLDExMi4yIDIwMSwxMTIuMiAxMDgsMTAuMSAgIDE0LjQsMTEyLjIgNTUuNiwxMTIuMiA1NS42LDE5Mi4xICIgc3Ryb2tlLW1pdGVybGltaXQ9IjEwIiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZT0iIzIzMUYyMCIgZmlsbD0ibm9uZSIvPjxnIGlkPSJzdmdfMyI+PHBhdGggaWQ9InN2Z180IiBkPSJtODAuMiwxMTguN2wxNSwwbDI3LjIsNDcuN2wwLC00Ny43bDEzLjIsMGwwLDY4LjRsLTE0LjMsMGwtMjcuOCwtNDguNWwwLDQ4LjVsLTEzLjEsMGwwLC02OC40bC0wLjIsMHoiIGZpbGw9IiMyMzFGMjAiLz48L2c+PC9nPjwvc3ZnPgo=',
  },
  {
    symbolId: 'symbol-UTurnStairsRight',
    symbolGroupId: 'staircases',
    name: 'U-Turn Stairs Right',
    /* eslint-disable max-len */
    data: 'data:image/svg+xml;base64,data:image/svg+xml;base64,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',
  },
];

const positionedSymbols: PositionedSymbol[] = [
  {
    positionedSymbolId: 'positionedSymbol1',
    symbolId: symbols[0].symbolId,
    pointId: 'p0',
  },
];

/* eslint-disable @typescript-eslint/explicit-function-return-type */
export function* loadTestModel() {
  yield all(pages.map(page => put(pagesActions.add(page))));
  yield put(areaTypesActions.set({ clear: false, areaTypes }));
  yield all(figures.map((figure: Figure) => put(figuresActions.add(figure))));
  yield all(walls.map((wall: Wall) => put(wallsActions.add(wall))));
  yield all(points.map(point => put(pointsActions.add(point))));
  yield all(labels.map(label => put(labelsActions.add(label))));
  yield all(positionedLabels.map(positionedLabel => put(positionedLabelActions.add(positionedLabel))));
  yield all(symbolGroups.map(symbol => put(symbolGroupActions.add(symbol))));
  yield all(symbols.map(symbol => put(symbolActions.add(symbol))));
  yield all(positionedSymbols.map(positionedSymbol => put(positionedSymbolActions.add(positionedSymbol))));
}
/* eslint-enable @typescript-eslint/explicit-function-return-type */
